<i18n>
{
    "uk": {
        "buy": "Придбати",
        "bySubscription": "За передплатою",
        "forFree": "Безплатно",
        "register": "Зареєструватись",
        "getAccess": "Отримати доступ",
        "customPriceFrom": "Від {minPrice} {currency}",
        "courseLanding": {
            "defaultTypeName": "Курс Креативної Практики"
        },
        "metaTitle": "Курс «{course}» від Креативної Практики"
    },

    "en": {
        "buy": "Buy",
        "bySubscription": "By subscription",
        "forFree": "Free",
        "register": "Register",
        "getAccess": "Get access",
        "customPriceFrom": "From {minPrice} {currency}",
        "courseLanding": {
            "defaultTypeName": "Creative Practice Course"
        },
        "metaTitle": "Course \"{course}\" by Creative Practice"
    }
}
</i18n>

<template>
    <section class="relative pb-6">
        <NavigationBar
            class="mb-0"
            :class="{
                minimal: introVisible && introBlock && introBlock.type == 'Hero2',
                'hide-center': introVisible && introBlock && introBlock.type !== 'Hero2'
            }"
        >
            <template v-slot:left>
                <BackButton
                    :to="localePath('/learning/courses')"
                    class="layer-2"
                />
            </template>

            <template v-slot:center>
                <div class="max-lines-1 fw-700 f-semismall">{{ course.name }}</div>
            </template>

            <template v-slot:right >
                <div class="d-flex">
                    <ShareButton
                        class="btn-bg btn-round layer-2"
                        id="share-post-mobile"
                        anchor="share-post-mobile"
                        :title="$t('general.share')"
                    >
                        <span class="icon-16 icon-ic-share" />
                    </ShareButton>

                    <nuxt-link
                        v-if="course.isModer"
                        :to="localePath(`/learning/course/${course.slug}/panel`)"
                        class="btn-bg btn-round layer-2 ml-1 ml-md-1h"
                        title="Панель керування"
                    >
                        <span class="icon-16 icon-ic-pencil-outline" />
                    </nuxt-link>
                </div>
            </template>
        </NavigationBar>

        <!-- Blocks -->
        <template v-for="(block, i) in course.blocks">
            <template v-if="!block.disabled">

                <Component
                    :is="block.type"
                    :block="block"
                    :key="`block-${block.type}`"
                    :class="isIntroBlock(block) ? '' : `transition-up-${i + 4}`"
                    @intersection="onIntersectionChange"
                />

            </template>
        </template>




        <client-only>
            <CasesPanel
                modal-name="installments"
                side="right"
                :noTitle="true"
                :classes="['wide-panel']"
            >
                <div class="installments-panel-intro bg-color-cadmium-yellow-dim">
                    <h4 class="gift-heading mt-0 mb-2 fw-800">Для цього курсу доступна оплата частинами від Приватбанку</h4>

                    <p>
                        Оплату на цей курс можна розбити на комфортну для вас кількість оплат: від 2 по 7500 грн/міс. (з передплатою 6750 грн/міс.)  до 5 оплат по 3000 грн/міс. (з передплатою 2700 грн/міс.)
                    </p>
                </div>

                <div class="p-4">
                    <p>
                        Щоб оформити оплату частинами за цей курс або по консультацію звертайтесь у будь-який зручний для вас спосіб.
                    </p>

                    <div class="gift-panel-actions mt-3 mx-n1">
                        <a
                            href="https://t.me/creativepractice"
                            class="gift-panel-action d-link"
                            target="_blank"
                        >
                            <span class="icon-ic-telegram-logo color-tg mr-1"></span>
                            {{ $t('support.telegram') }}
                        </a>

                        <a
                            href="https://m.me/creativepractice.com.ua"
                            class="gift-panel-action d-link"
                            target="_blank"
                        >
                            <span class="icon-ic-facebook-circle color-fb mr-1"></span>
                            {{ $t('support.facebook') }}
                        </a>
                    </div>
                </div>
            </CasesPanel>


            <CasesPanel
                modal-name="gift"
                side="right"
                :noTitle="true"
                :classes="['wide-panel']"
            >
                <div class="gift-panel-intro bg-color-purple-dim">
                    <h4 class="gift-heading mt-0 mb-2 fw-800">Ви можете подарувати абонемент</h4>

                    <p>
                        Після оплати ми згенеруємо для вас електронний персоналізований подарунковий сертифікат.
                    </p>

                    <div class="mt-3">
                        <a
                            href="/images/creative-practice/val-2022/certificate-example-2.png"
                            target="_blank"
                            class="btn-primary d-inline-flex"
                            download="Certificate Example.png"
                        >
                            Завантажити зразок
                        </a>
                    </div>
                </div>

                <div class="p-4">
                    <p>
                        Зв’яжіться з нами зручним способом, ми уточнимо деталі і все організуємо!
                    </p>

                    <div class="gift-panel-actions mt-3 mx-n1">
                        <a
                            href="https://t.me/creativepractice"
                            class="gift-panel-action d-link"
                            target="_blank"
                        >
                            <span class="icon-ic-telegram-logo color-tg mr-1"></span>
                            {{ $t('support.telegram') }}
                        </a>

                        <a
                            href="https://m.me/creativepractice.com.ua"
                            class="gift-panel-action d-link"
                            target="_blank"
                        >
                            <span class="icon-ic-facebook-circle color-fb mr-1"></span>
                            {{ $t('support.facebook') }}
                        </a>
                    </div>
                </div>
            </CasesPanel>



            <!-- Scroll top button -->
            <portal to="sticky-panel" ref="portal">
                <button
                    :class="{'hidden': introVisible}"
                    v-scroll-to="'.content-container'"
                    class="course-scroll-top-button btn-primary"
                >
                    <span class="course-scroll-top-icon icon-ic-arrow-down" />
                </button>
            </portal>
        </client-only>


        <!-- Top bar -->
        <div
            v-if="canSubscribe && !course.bought && !course.closeEnroll"
            class="top-promo-bar d-none d-lg-flex mt-6"
        >

            <div
                class="bar d-flex"
                :class="{show: showTopBar}"
            >
                <div class="img mr-3">
                    <WebpPicture
                        :src="course.thumbnail"
                        alt=""
                        loading="lazy"
                        decoding="async"
                    />
                </div>

                <div>
                    <div class="fw-700 name">{{ course.name }}</div>
                    <div class="f-small color-secondary">{{ course.landingType || $t('courseLanding.defaultTypeName', locale) }}</div>
                </div>

                <div class="mx-3 separator"></div>

                <div v-if="!isFree" class="mr-3">
                    <div class="fw-700">
                        <template v-if="course.price && course.price != 0">
                            <strike v-if="course.priceDiscount" class="striked">{{ course.price }}</strike>
                            {{ course.priceDiscount || course.price }}
                            {{ (introBlock && introBlock.price && introBlock.price.suffix) || (introBlock && introBlock.priceSuffix) || $t('general.uah') }}
                        </template>
                        <template v-else-if="isCustomPrice">
                            {{ $t('customPriceFrom', {
                                minPrice: course.minPrice,
                                currency: $t('general.uah')
                            }) }}
                        </template>
                    </div>
                    <div class="f-small color-secondary" v-html="(introBlock && introBlock.price && introBlock.price.comment) || (introBlock && introBlock.priceComment) || course.priceComment" />
                </div>
                <div v-else-if="course.subscriptionOnly" class="mr-3 align-self-center">
                    <div class="fw-700">{{ $t('bySubscription', locale) }}</div>
                </div>
                <div v-else class="mr-3">
                    <div v-if="isFree" class="fw-700">{{ $t('forFree', locale) }}</div>
                    <div class="f-small color-secondary" v-html="(introBlock && introBlock.price && introBlock.price.comment) || (introBlock && introBlock.priceComment) || course.priceComment" />
                </div>

                <button
                    v-scroll-to="{el: applyBlock ? '#apply' : '#subscribe', offset: linkOffset}"
                    class="btn-primary tt-uppercase ml-auto fw-700"
                >
                    <span v-if="!applyBlock && !isFree && !isCustomPrice" class="icon-ic-cart-outline color-gray-3 mr-1 incon" />
                    <template v-if="applyBlock">{{ $t('course.preregistration') }}</template>
                    <template v-else-if="isFree">{{ $t('register', locale) }}</template>
                    <template v-else-if="isCustomPrice">{{ $t('getAccess', locale) }}</template>
                    <template v-else>{{ $t('buy', locale) }}</template>
                </button>
            </div>
        </div>

        <client-only>
            <Share placement="bottom-end"/>
        </client-only>

    </section>
</template>


<script>
    // const cpCompanyId = '5c6426f120e2ed2f536d63cf'; // Creative Practice (hardcoded)


    import accessPlans from '~/mixins/course-landing/accessPlans.js';
    import WebpPicture from '~/components/WebpPicture.vue';
    import NavigationBar from '~/components/navigation/NavigationBar.vue';
    import ShareButton   from '~/components/share/ShareButton.vue';
    import BackButton    from '~/components/BackButton.vue';


    import { useCourseLandingStore } from '~/stores/courseLanding';
    import { platformPricing } from '~/utilities/pricing';


    export default {
        scrollToTop: true,

        components: {
            Slides: () => import('~/components/creative-practice/course-landing/Slides.vue'),
            Cases: () => import('~/components/creative-practice/course-landing/Cases.vue'),
            ProgramGroup: () => import('~/components/creative-practice/course-landing/ProgramGroup.vue'),
            Program: () => import('~/components/creative-practice/course-landing/Program.vue'),
            CasesPanel: () => import('~/components/panels/CasesPanel.vue'),
            Hero: () => import('~/components/creative-practice/course-landing/Hero.vue'),
            Hero2: () => import('~/components/creative-practice/course-landing/Hero2.vue'),
            EventHero: () => import('~/components/creative-practice/course-landing/EventHero.vue'),
            HeroPartner: () => import('~/components/creative-practice/course-landing/HeroPartner.vue'),
            Tiles: () => import('~/components/creative-practice/course-landing/Tiles.vue'),
            FeatureTiles: () => import('~/components/creative-practice/course-landing/FeatureTiles.vue'),
            Chips: () => import('~/components/creative-practice/course-landing/Chips.vue'),
            School: () => import('~/components/creative-practice/course-landing/School.vue'),
            Teachers: () => import('~/components/creative-practice/course-landing/Teachers.vue'),
            IconList: () => import('~/components/creative-practice/course-landing/IconList.vue'),
            ResultList: () => import('~/components/creative-practice/course-landing/ResultList.vue'),
            Intro: () => import('~/components/creative-practice/course-landing/Intro.vue'),
            IntroMentor: () => import('~/components/creative-practice/course-landing/IntroMentor.vue'),
            IntroLegacy: () => import('~/components/creative-practice/course-landing/IntroLegacy.vue'),
            About: () => import('~/components/creative-practice/course-landing/About.vue'),
            AboutCard: () => import('~/components/creative-practice/course-landing/AboutCard.vue'),
            AboutLegacy: () => import('~/components/creative-practice/course-landing/AboutLegacy.vue'),
            CompanyTiles: () => import('~/components/creative-practice/course-landing/CompanyTiles.vue'),
            UserTiles: () => import('~/components/creative-practice/course-landing/UserTiles.vue'),
            Awards: () => import('~/components/creative-practice/course-landing/Awards.vue'),
            AboutTiles: () => import('~/components/creative-practice/course-landing/AboutTiles.vue'),
            ProgramTable: () => import('~/components/creative-practice/course-landing/ProgramTable.vue'),
            FaqPlain: () => import('~/components/creative-practice/course-landing/FaqPlain.vue'),
            Curators: () => import('~/components/creative-practice/course-landing/Curators.vue'),
            MonthesProgram: () => import('~/components/creative-practice/course-landing/MonthesProgram.vue'),
            LearningMap: () => import('~/components/creative-practice/course-landing/LearningMap.vue'),
            Card: () => import('~/components/creative-practice/course-landing/Card.vue'),
            Podcast: () => import('~/components/creative-practice/course-landing/Podcast.vue'),
            Portraits: () => import('~/components/creative-practice/course-landing/Portraits.vue'),
            Lecturers: () => import('~/components/creative-practice/course-landing/Lecturers.vue'),
            Mentor: () => import('~/components/creative-practice/course-landing/Mentor.vue'),
            ImagedText: () => import('~/components/creative-practice/course-landing/ImagedText.vue'),
            Audience: () => import('~/components/creative-practice/course-landing/Audience.vue'),
            ProgramLegacy: () => import('~/components/creative-practice/course-landing/ProgramLegacy.vue'),
            Theory: () => import('~/components/creative-practice/course-landing/Theory.vue'),
            Practice: () => import('~/components/creative-practice/course-landing/Practice.vue'),
            Works: () => import('~/components/creative-practice/course-landing/Works.vue'),
            HowItWorks: () => import('~/components/creative-practice/course-landing/HowItWorks.vue'),
            Recommended: () => import('~/components/creative-practice/course-landing/Recommended.vue'),
            Structure: () => import('~/components/creative-practice/course-landing/Structure.vue'),
            Samples: () => import('~/components/creative-practice/course-landing/Samples.vue'),
            Apply: () => import('~/components/creative-practice/course-landing/Apply.vue'),
            AuthorsAndTeam: () => import('~/components/creative-practice/course-landing/AuthorsAndTeam.vue'),
            Partners: () => import('~/components/creative-practice/course-landing/Partners.vue'),
            Reviews: () => import('~/components/creative-practice/course-landing/Reviews.vue'),
            Subscribe: () => import('~/components/creative-practice/course-landing/Subscribe.vue'),
            SubscribeLegacy: () => import('~/components/creative-practice/course-landing/SubscribeLegacy.vue'),
            Phone: () => import('~/components/creative-practice/course-landing/Phone.vue'),
            Faq: () => import('~/components/creative-practice/course-landing/Faq.vue'),
            FaqCollapse: () => import('~/components/creative-practice/course-landing/FaqCollapse.vue'),
            CustomPrice: () => import('~/components/creative-practice/course-landing/CustomPrice.vue'),
            ComeBackAlive: () => import('~/components/creative-practice/course-landing/ComeBackAlive.vue'),
            Video: () => import('~/components/creative-practice/course-landing/Video.vue'),
            Description: () => import('~/components/creative-practice/course-landing/Description.vue'),
            EventProgram: () => import('~/components/creative-practice/course-landing/EventProgram.vue'),
            Manual: () => import('~/components/creative-practice/course-landing/Manual.vue'),

            WebpPicture,
            NavigationBar,
            ShareButton,
            BackButton,
            Share: () => import('~/components/share/ShareDropdown.vue')
        },


        head () {
            let title = (this.course.meta && this.course.meta.title) || this.$t('metaTitle', this.locale, {
                course: this.course.name,
            });
            let desc  = this.course.meta && this.course.meta.description;
            let url   = `${process.env.baseUrl}/learning/course/${this.course.slug}/about`;
            let image = (this.course.meta && this.course.meta.image) || this.course.thumbnail;


            let meta = [
                { hid: 'title', name: "title", content: title },

                { hid: "og:url",   property: "og:url",   content: url },
                { hid: "og:title", property: "og:title", content: title },
                { hid: "og:image", property: "og:image", content: image },

                { hid: "twitter:url",   name: "twitter:url",   content: url },
                { hid: "twitter:title", name: "twitter:title", content: title },
                { hid: "twitter:image", name: "twitter:image", content: image }
            ];


            let link = [
                { hid:"vimeo-preconnect", rel:"preconnect", href: "https://player.vimeo.com"}
            ];

            if (desc) {
                meta.push({ hid: 'description',         name: 'description',         content: desc });
                meta.push({ hid: "og:description",      property: "og:description",  content: desc });
                meta.push({ hid: "twitter:description", name: "twitter:description", content: desc });
            }

            let levelMatrix = {
                success: "Beginner",
                yellow: "Intermediate",
                error: "Advanced",
                purple: "Advanced",

                beginner: "Beginner",
                intermediate: "Intermediate",
                advanced: "Advanced",
            }

            let structuredData = {
                "@context":"http://schema.org",
                "@graph": [
                    {
                        "@type":"BreadcrumbList",
                        "itemListElement": [
                            {
                                "@type":"ListItem",
                                "position": 1,
                                "item": "https://cases.media/learning",
                                "name":"Creative Practice",
                            },
                            {
                                "@type": "ListItem",
                                "position": 2,
                                "item": "https://cases.media/learning/courses",
                                "name": this.$t('menu.courses')
                            },
                            {
                                "@type": "ListItem",
                                "position": 3,
                                "name": this.course.name
                            }
                        ]
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "Course",
                        "@id": url,
                        "name": this.course.name,
                        "image": image,
                        "description": this.course.meta && this.course.meta.description,
                        "isAccessibleForFree": `http://schema.org/${this.isFree ? "True" : "False"}`,
                        "inLanguage": "uk",
                        "availability": "OnlineOnly",
                        "educationalCredentialAwarded": "Creative Practice Certificate",
                        "provider": {
                            "@type": "Organization",
                            "name": "Creative Practice",
                            "url":"https://cases.media/learning"
                        },
                        "hasCourseInstance": [{
                            "@type": "CourseInstance",
                            "courseMode": "Online",
                            // "courseWorkload": "P2D",
                            // "instructor": [{
                            //     "@type": "Person",
                            //     "name": "Kai S.",
                            //     "description": "Professor at X-University",
                            //     "image": "http://examplePerson.jpg"
                            // }]
                        }],
                        "inLanguage": this.course.landingLocale,
                        "totalHistoricalEnrollment": this.course.students
                    }
                ]
            }

            if (this.course.levels && this.course.levels[0]) {
                let level = this.course.levels[0];
                if (typeof level != 'string') level = level?.color;
                if (levelMatrix[level]) {
                    structuredData["@graph"][1].educationalLevel = levelMatrix[level];
                }
            }

            if (this.course.program) {
                let duration = 0;

                this.course.program.forEach(obj => {
                    if (!obj.module || !obj.module.duration) return;
                    duration += parseInt(obj.module.duration);
                });

                structuredData["@graph"][1].hasCourseInstance[0].courseWorkload = `PT${duration}H`;
            }

            if (this.course.rating.length) {
                structuredData["@graph"][1].aggregateRating = {
                    "@type":"AggregateRating",
                    "ratingValue": this.course.rating.average,
                    "reviewCount": this.course.rating.length
                }
            }


            if (this.course.price == 0) {
                structuredData["@graph"][1].offers = [{
                    "@type": "Offer",
                    "category": "Free"
                }]
            } else {
                structuredData["@graph"][1].offers = [{
                    "@type": "Offer",
                    "category": "Subscription",
                    "priceCurrency": "UAH",
                    "price": platformPricing.subscriptionFullAccess || 550
                }]
            }



            if (this.course.slugMap) {
                for (let code in this.course.slugMap) {
                    link.push({ rel: "alternate", href: this.localePath(`/learning/course/${this.course.slugMap[code]}/about`, code), hreflang: code })
                }
            }

            // Disable ld+json for events
            let scripts = [];
            if (this.course.type !== 'event') {
                scripts.push({
                    type: 'application/ld+json',
                    json: structuredData
                })
            }

            return {
                title,
                link,
                meta,
                script: scripts
            }
        },

        data () {
            return {
                linkOffset: -72,

                course:      {},
                reviews:     {},
                maps:        [],
                plans:       [],
                planCourses: [],

                // companyId: cpCompanyId,

                introVisible: true,
                pricingVisible: false,

                fbPricingView: false,

                clearStoreOnDestroyed: false
            }
        },

        async asyncData({ $axios, route, app, redirect, error, $appStore }) {
            let course = route.params?.course || route.params?.slug;

            let datas = await Promise.all([
                $axios.$get(`${$axios.defaults.baseURL}/course/about`, {
                    params: { slug: course }
                }),
                $axios.$get(`${$axios.defaults.baseURL}/access/getTariffs`)
            ])
            .catch((e) => {
                error({ statusCode: 404, message: 'Курс не знайдено' })
            });

            if (!datas) {
                return error({ statusCode: 404, message: 'Курс не знайдено' })
            }

            let [data, planData] = datas;


            if (!planData) {
                return error({ statusCode: 404, message: 'Сторінку не знайдено' });
            }

            const isEvent      = Boolean(data.course?.type == 'event');
            const isEventRoute = Boolean('slug' in route.params);

            if (isEvent != isEventRoute) {
                return redirect(app.localePath(isEvent ? `/event/${course}/about` : `/learning/course/${course}/about`));
            }

            $appStore.$patch({activeApp: isEvent ? 'events' : 'learning'});

            // Build plans array
            let plans = accessPlans.methods.mergeAccessPlans(
                planData,
                accessPlans.computed.defaultAccessPlans()
            );


            let result = {
                course:  data.course,
                maps:    data.maps,
                reviews: data.rates || [],
                plans,
                planCourses: planData.courses,
            };

            return result;
        },


        computed: {
            locale () {
                return this.courseLandingStore.locale;
            },

            availableLocales () {
                return this.$i18n.locales;
            },

            availableLocalesMap () {
                return this.availableLocales.map(o => o.code);
            },

            isFree () {
                return !this.course.price
                    && !this.course.useCustomPrice;
            },

            isCustomPrice () {
                return this.course.useCustomPrice;
            },

            canSubscribe () {
                return this.course.blocks.some(block => {
                    if (block.disabled) return false;
                    const type = block.type.toLowerCase();
                    return type == 'subscribe'
                        || type == 'subscribelegacy'
                        || type == 'customprice'
                        || type == 'comebackalive';
                });
            },

            showTopBar () {
                return !this.introVisible && !this.pricingVisible;
            },

            applyBlock () {
                return this.fetchBlockType('Apply');
            },

            introBlock () {
                return this.fetchBlock('intro');
            },


            courseLandingStore () {
                return useCourseLandingStore();
            },
        },

        created () {
            this.setStoreData();
            this.setStoreLocale();
        },

        mounted () {
            if (process.browser) {
                this.setStoreLocale();
                this.$fbPixel.trackSingle('ViewContent', {
                    // This should be product or product_group, see docs: https://developers.facebook.com/docs/facebook-pixel/reference#object-properties
                    'content_type': 'product',
                    'content_ids':  ['course:' + this.course.id],
                    'content_name': this.course.name
                });
            }
        },

        updated () {
            if (this.hasScrolled) return;

            const hash = 'hash' in window.location
                ? window.location.hash
                : String(window.location).match(/(?<hash>#.+)$/)?.groups?.hash;

            if (!hash) return;

            const isPresent = document.querySelector(hash);

            if (!isPresent) return;

            if (this.scrollTimeout) window.clearTimeout(this.scrollTimeout);

            this.scrollTimeout = window.setTimeout(() => {
                this.hasScrolled = true;
                this.$scrollTo(hash, {
                    offset: this.linkOffset
                });
            }, 100);
        },

        // Check if next page is course landing
        beforeRouteLeave (to, from, next) {
            const regex = /^\/(?:[^\/]+\/)?learning\/course\/(?:[^\/]+)\/about/;

            if (!regex.test(to.path)) this.clearStoreOnDestroyed = true;

            return next();
        },

        destroyed () {
            // Clear storage if next page isnt course landing
            if (this.clearStoreOnDestroyed) this.unsetStoreData();
        },

        methods: {
            setStoreData () {
                this.courseLandingStore.$patch({
                    course:      this.course,
                    maps:        this.maps,
                    reviews:     this.reviews,
                    plans:       this.plans,
                    planCourses: this.planCourses,
                });

                this.maps = [];
                this.reviews = {};
                this.plans = [];
                this.planCourses = [];
            },

            setStoreLocale () {
                let locale = this.$route.query.locale;

                if (!locale || !this.availableLocalesMap.indexOf(locale) >= 0) locale = this.$i18n.locale;

                this.courseLandingStore.setLocale(locale);
            },

            unsetStoreData () {
                this.courseLandingStore.$reset();
            },

            fetchBlock (id) {
                return this.course.blocks.find(block => {
                    // Skip disabled blocks
                    if (block.disabled) return false;

                    return block.id == id;
                });
            },

            fetchBlockType (type) {
                let types;

                if (!Array.isArray(type)) {
                    types = [type];
                }

                types = types.map(item => item.toLowerCase());

                return this.course.blocks.find(block => {
                    // Skip disabled blocks
                    if (block.disabled) return false;

                    const type = block.type.toLowerCase();

                    return types.includes(type);
                });
            },

            isIntroBlock (block) {
                const type = block.type.toLowerCase();
                return [
                    'hero',
                    'hero2',
                    'introlegacy',
                    'intromentor',
                    'intro'
                ].includes(type);
            },

            isBuyBlock (block) {
                const type = block.type.toLowerCase();
                return [
                    'subscribe',
                    'subscribelegacy',
                    'customprice',
                    'comebackalive'
                ].includes(type);
            },

            onIntersectionChange (data) {
                if (this.isIntroBlock({ type: data.type })) {
                    this.introVisible = data.isVisible;
                }

                if (this.isBuyBlock({ type: data.type })) {
                    this.pricingVisible = data.isVisible;

                    if (this.pricingVisible && !this.fbPricingView) {
                        this.fbPricingView = true;
                    }
                }
            }
        }
    }
</script>


<style lang="scss">
    .wide-panel {
        @media screen and (min-width: 576px) {
            width: 460px;
        }
    }
</style>


<style lang="scss" scoped>
    @media screen and (max-width: 992px) {
        :deep(.hero-2_1) {
            margin-top: calc(var(--navigation-bar-height) * -1);
        }
    }

    .top-promo-bar {
        position: sticky;
        bottom: 16px;
        z-index: 100;
        // height: 0px;
        will-change: transform;
        pointer-events: none;

        .striked {color: #a8b0bf}

        .bar {
            max-width: 800px;
            min-width: 608px;
            height: 64px;
            padding: 8px;

            margin: 0 auto;

            // background-color: var(--color-control);
            background: rgba(97, 95, 103, 0.95);


            // box-shadow: 0px 2px 6px var(--color-cases-shadow-soft);
            border-radius: 64px;

            opacity: 0;
            transform: translateY(20px);
            pointer-events: none;
            overflow: hidden;

            color: #fff;
            transition: opacity 0.15s ease,
                        transform 0.25s ease;

            &.show {
                opacity: 1;
                pointer-events: all;
                transform: translateY(0px);
            }
        }

        .separator {
            flex-shrink: 0;
            width: 1px;
            height: 100%;
            background-color: #878D99;
        }

        .color-secondary {color: #F5F7FA !important;}

        .incon {font-size: 20px;}

        .img {
            flex-grow: 0;
            flex-shrink: 0;
            border-radius: 64px;
            overflow: hidden;
            flex-basis: 85px;
            width: 85px;

            img {
                width: 100%;
                display: block;
            }
        }

        .name {
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 169px;
            flex-basis: 169px;
            overflow: hidden;
        }
    }

    .safari {
        .bar {
            background: rgba(14, 16, 20, 0.64);
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
        }

        .border {
            display: block;
            height: 1px;
            width: 100%;
            background-color: var(--color-cases-black);
            opacity: 0.08;
        }
    }

    .relative {
        position: relative;
    }

    .gift-panel-intro {
        background: url(/images/creative-practice/val-2022/gift-panel-illu.png) no-repeat 32px 100%;
        background-size: 319px 194px;

        padding: 32px 32px (194px + 24px);
    }

    .gift-card-bg {
        background: url(/images/creative-practice/val-2022/gift-panel-illu.png) no-repeat 32px 100%;
        background-size: 319px 194px;

        padding-bottom: 194px + 32px;
    }

    .installments-panel-intro {
        background: url(/images/creative-practice/about-common/sub-panel-installment-img.png) no-repeat 50% 100%;
        background-size: 213px 202px;

        padding: 32px 32px (202px + 24px);
    }

    .gift-heading {
        font-size: 19px;
    }

    .gift-panel-actions {
        display: flex;
        flex-flow: column nowrap;
        align-items: stretch;
    }

    .gift-panel-action {
        text-align: center;

        background-color: var(--color-cases-gray-5);

        border-radius: 8px;
        padding:       12px 16px;

        & + & {
            margin-top: 8px;
        }

        .color-tg {
            color: #1B8BE0;
        }

        .color-fb {
            color: #157FFB;
        }
    }
</style>
